/* oxlint-disable tailwindcss/no-unknown-classes -- Styles are colocated in home-new.css. */
import { Brain, FileText, MessagesSquare, Waypoints, type LucideIcon } from "lucide-react"
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from "react"

import { agents, type AgentConfig } from "@/lib/agents"

import { elevatedRingClassName } from "../homeNewStyles"
import { AgentAvatar, PersonAvatar, roundedPath, type Point } from "../SessionLinksShowcase"
import { teamAvatars } from "../teamAvatars"
import { useRevealOnce } from "../useRevealOnce"
import { illustrationCardClassName } from "./illustrationKit"

// The Continuous Memory claims: entire-brain as a hub that sessions, facts,
// docs and the code graph feed, and that every agent reads from. The links
// are the code hosting scene's flowing dashes, measured from the live layout
// so each scene reflows with its claim instead of scaling. The pieces fade in
// (opacity only, so the links measure against resting positions) while the
// links draw. Styles: `.home-new-brain-scene` in home-new.css.

interface Box {
  left: number
  right: number
  top: number
  bottom: number
  cx: number
  cy: number
}

type Measure = (element: HTMLElement | null | undefined) => Box | null

/** Measures `route` (kept stable with useCallback) against the scene's live layout whenever it resizes. */
function useMeasuredLinks(
  scene: RefObject<HTMLDivElement | null>,
  route: (box: Measure) => string[],
) {
  const [geometry, setGeometry] = useState<{
    width: number
    height: number
    links: string[]
  } | null>(null)

  useLayoutEffect(() => {
    const sceneElement = scene.current
    if (!sceneElement) return
    const measure = () => {
      const base = sceneElement.getBoundingClientRect()
      const box = (element: HTMLElement | null | undefined): Box | null => {
        if (!element) return null
        const rect = element.getBoundingClientRect()
        const left = rect.left - base.left
        const top = rect.top - base.top
        return {
          left,
          right: left + rect.width,
          top,
          bottom: top + rect.height,
          cx: left + rect.width / 2,
          cy: top + rect.height / 2,
        }
      }
      setGeometry({ width: base.width, height: base.height, links: route(box) })
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(sceneElement)
    return () => observer.disconnect()
  }, [scene, route])

  return geometry
}

/** The measured links as flowing dashes that draw in one after another. */
function DashedLinks({
  id,
  geometry,
  delay = 300,
}: {
  id: string
  geometry: { width: number; height: number; links: string[] }
  delay?: number
}) {
  return (
    <svg
      className="pointer-events-none absolute inset-0 overflow-visible text-black/25 dark:text-white/35"
      width={geometry.width}
      height={geometry.height}
      viewBox={`0 0 ${geometry.width} ${geometry.height}`}
    >
      {geometry.links.map((d, index) => (
        <g key={index}>
          <mask id={`${id}-${index}`} maskUnits="userSpaceOnUse">
            <path
              d={d}
              pathLength={1}
              stroke="white"
              strokeWidth={4}
              fill="none"
              className="home-new-mirror-draw"
              style={{ animationDelay: `${delay + index * 120}ms`, animationDuration: "700ms" }}
            />
          </mask>
          <path
            d={d}
            fill="none"
            stroke="currentColor"
            strokeDasharray="4 4"
            strokeLinejoin="round"
            mask={`url(#${id}-${index})`}
            className="home-new-mirror-dash"
          />
        </g>
      ))}
    </svg>
  )
}

/** entire-brain: the round hub both scenes connect to. */
function BrainHub({
  ref,
  className = "",
}: {
  ref: RefObject<HTMLDivElement | null>
  className?: string
}) {
  return (
    <div
      ref={ref}
      data-brain-part
      style={{ "--part": 0 } as CSSProperties}
      className={`flex size-10 items-center justify-center rounded-full ${illustrationCardClassName} ${className}`}
    >
      <Brain size={16} strokeWidth={1.33} className="text-default" />
    </div>
  )
}

const SOURCES = ["Sessions", "Facts", "Docs", "Graph"]

// What Georg's new session already knows before the first prompt: Peyton's
// proration decision, the billing doc and the graph's link between files.
const MEMORIES: Array<{ Icon: LucideIcon; text: string }> = [
  { Icon: MessagesSquare, text: "Prorate seats by day, round down" },
  { Icon: FileText, text: "docs/billing/proration.md" },
  { Icon: Waypoints, text: "plan.ts → invoice.ts" },
]

/**
 * "Agents get smarter with every session": captured sessions, facts, docs and
 * the code graph flow into entire-brain, and a new session starts with what
 * it needs already loaded.
 */
export function BrainSourcesIllustration() {
  const [revealed, ref] = useRevealOnce()
  const scene = useRef<HTMLDivElement>(null)
  const brain = useRef<HTMLDivElement>(null)
  const sources = useRef<Array<HTMLDivElement | null>>([])
  const session = useRef<HTMLDivElement>(null)
  const route = useCallback((box: Measure) => {
    const hub = box(brain.current)
    const card = box(session.current)
    if (!hub || !card) return []
    const links = sources.current.map((element) => {
      const source = box(element)
      if (!source) return ""
      const elbow = (source.bottom + hub.top) / 2
      return roundedPath([
        { x: source.cx, y: source.bottom },
        { x: source.cx, y: elbow },
        { x: hub.cx, y: elbow },
        { x: hub.cx, y: hub.top },
      ] satisfies Point[])
    })
    return [
      ...links,
      roundedPath([
        { x: hub.cx, y: hub.bottom },
        { x: hub.cx, y: card.top },
      ]),
    ]
  }, [])
  const geometry = useMeasuredLinks(scene, route)

  return (
    <div
      ref={ref}
      data-revealed={revealed ? "true" : "false"}
      aria-hidden="true"
      inert
      className="home-new-brain-scene w-full min-w-0 font-sans"
    >
      <div
        ref={scene}
        className="relative flex h-[244px] w-full flex-col items-center justify-between"
      >
        {geometry && revealed ? (
          <DashedLinks id="home-new-brain-source" geometry={geometry} delay={200} />
        ) : null}

        <div className="flex w-full gap-2">
          {SOURCES.map((label, index) => (
            <div
              key={label}
              ref={(element) => void (sources.current[index] = element)}
              data-brain-part
              style={{ "--part": index } as CSSProperties}
              className={`relative flex h-7 min-w-0 flex-1 items-center justify-center rounded-lg px-2 ${illustrationCardClassName}`}
            >
              <span className="truncate text-xs leading-4 font-medium text-default">{label}</span>
            </div>
          ))}
        </div>

        <BrainHub ref={brain} className="relative" />

        <div
          ref={session}
          data-brain-part
          style={{ "--part": 5 } as CSSProperties}
          className={`relative w-full rounded-lg bg-floating p-1 ${elevatedRingClassName}`}
        >
          <div className="flex min-h-8 items-center gap-2 px-2">
            <span className="isolate flex shrink-0 items-center">
              <span className="relative z-10 -mr-1 flex">
                <PersonAvatar src={teamAvatars.georg} ringed={false} />
              </span>
              <AgentAvatar agent={agents.claude} />
            </span>
            <p className="min-w-0 flex-1 truncate text-sm leading-5 font-medium text-default">
              New session
            </p>
            <span className="shrink-0 text-xs leading-4 text-muted">Starts with 3 memories</span>
          </div>
          <ul className="m-0 list-none">
            {MEMORIES.map(({ Icon, text }, index) => (
              <li
                key={text}
                data-brain-part
                style={{ "--part": 6 + index } as CSSProperties}
                className="flex min-h-7 items-center gap-2 px-2"
              >
                <Icon size={16} strokeWidth={1.33} className="shrink-0 text-muted" />
                <span className="min-w-0 truncate text-sm leading-5 text-default">{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

type Side = "left" | "right" | "top" | "bottom"

// Chip centres as a share of the scene, and which side of the brain each
// link enters, after the Figma layout.
const SESSIONS: Array<{
  name: string
  image: string
  agent: AgentConfig
  x: number
  y: number
  side: Side
}> = [
  { name: "Pat", image: teamAvatars.pat, agent: agents.claude, x: 24, y: 16, side: "left" },
  { name: "Nina", image: teamAvatars.nina, agent: agents.codex, x: 66, y: 22, side: "top" },
  {
    name: "Andrea",
    image: teamAvatars.andrea,
    agent: agents.pi,
    x: 85,
    y: 58,
    side: "right",
  },
  {
    name: "Peter",
    image: teamAvatars.peter,
    agent: agents.amp,
    x: 57,
    y: 86,
    side: "bottom",
  },
  {
    name: "Kai",
    image: teamAvatars.kai,
    agent: agents.opencode,
    x: 16,
    y: 68,
    side: "left",
  },
]

/**
 * "One brain for the whole team": five teammates' sessions, each with a
 * different agent, feed one shared brain in the middle, each link routed to
 * its side of the brain with orthogonal, rounded elbows.
 */
export function TeamBrainIllustration() {
  const [revealed, ref] = useRevealOnce()
  const scene = useRef<HTMLDivElement>(null)
  const brain = useRef<HTMLDivElement>(null)
  const chips = useRef<Array<HTMLDivElement | null>>([])
  const route = useCallback((box: Measure) => {
    const hub = box(brain.current)
    if (!hub) return []
    const r = (hub.right - hub.left) / 2
    return SESSIONS.map(({ side }, index) => {
      const chip = box(chips.current[index])
      if (!chip) return ""
      let points: Point[]
      if (side === "left" || side === "right") {
        const start = { x: side === "left" ? chip.right : chip.left, y: chip.cy }
        const end = { x: side === "left" ? hub.cx - r : hub.cx + r, y: hub.cy }
        const elbow = (start.x + end.x) / 2
        points = [start, { x: elbow, y: start.y }, { x: elbow, y: end.y }, end]
      } else if (side === "top") {
        const start = { x: chip.left, y: chip.cy }
        points = [start, { x: hub.cx, y: start.y }, { x: hub.cx, y: hub.cy - r }]
      } else {
        const start = { x: chip.cx, y: chip.top }
        const end = { x: hub.cx, y: hub.cy + r }
        const elbow = (start.y + end.y) / 2
        points = [start, { x: start.x, y: elbow }, { x: end.x, y: elbow }, end]
      }
      return roundedPath(points)
    })
  }, [])
  const geometry = useMeasuredLinks(scene, route)

  return (
    <div
      ref={ref}
      data-revealed={revealed ? "true" : "false"}
      aria-hidden="true"
      inert
      className="home-new-brain-scene w-full min-w-0"
    >
      <div ref={scene} className="relative h-[244px] w-full">
        {geometry && revealed ? <DashedLinks id="home-new-team-brain" geometry={geometry} /> : null}

        <BrainHub ref={brain} className="absolute top-1/2 left-1/2 -translate-1/2" />

        {SESSIONS.map(({ name, image, agent, x, y }, index) => (
          <div
            key={name}
            ref={(element) => void (chips.current[index] = element)}
            data-brain-part
            style={{ left: `${x}%`, top: `${y}%`, "--part": index + 1 } as CSSProperties}
            className={`absolute flex -translate-1/2 items-center px-3 py-2 ${illustrationCardClassName}`}
          >
            <span className="isolate flex items-center">
              <span className="relative z-10 -mr-1 flex">
                <PersonAvatar src={image} ringed={false} />
              </span>
              <AgentAvatar agent={agent} />
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
